import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import LoadingSpinner from "@/components/common/common/LoadingSpinner";
import { FileData } from "@/components/console/form/FileUpload";
import TitleBox from "@/components/user/common/TitleBox";
import { useGetBoard, useGetBoardFileDownload } from "@/service/user/board";
import { usePopupStore } from "@/store/common/usePopupStore";

interface InfoItem {
    b_title: string;
    m_name: string;
    b_reg_date: string;
    b_contents: string;
    b_file: FileData[];
}

export default function BoardDetail() {
    const { category } = useParams();
    const navigate = useNavigate();
    const { board_idx } = useParams();
    const [downloadFile, setDownloadFile] = useState<{ idx: string; name: string } | null>(null);
    const { data: configData, isLoading: isInitialLoading } = useGetBoard(category || "", board_idx || "", {
        enabled: Boolean(category && board_idx),
    });
    const { data: downloadData, isLoading: isFileDownloadLoading } = useGetBoardFileDownload(
        category || "",
        board_idx || "",
        downloadFile?.idx || "",
    );
    const initialInfo = useMemo<InfoItem>(
        () => ({
            b_title: "",
            m_name: "",
            b_reg_date: "",
            b_contents: "",
            b_file: [],
        }),
        [],
    );
    const [info, setInfo] = useState<InfoItem>(initialInfo);
    const { setLoadingPop } = usePopupStore();

    // 데이터 로딩 중일 때 로딩 팝업 표시
    useEffect(() => {
        setLoadingPop(isFileDownloadLoading, true);
        return () => setLoadingPop(false);
    }, [isFileDownloadLoading, setLoadingPop]);

    // 게시글 상세 조회
    useEffect(() => {
        if (configData) {
            setInfo(configData.data);
        } else {
            setInfo(initialInfo);
        }
    }, [configData, initialInfo]);

    // 파일다운로드 데이터가 있을 때 실행
    useEffect(() => {
        if (downloadData && downloadFile) {
            const url = window.URL.createObjectURL(new Blob([downloadData.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", downloadFile.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDownloadFile(null); // 다운로드 완료 후 초기화
        }
    }, [downloadData, downloadFile]);

    // 첨부파일 다운로드 버튼 클릭시
    const handleFileDownload = (idx: string, file_name: string) => {
        if (!board_idx) return;
        setDownloadFile({ idx, name: file_name });
    };

    return (
        <div className="px-[20px] xl:px-0 max-w-[1400px] w-full mx-auto">
            <TitleBox
                title={category === "166" ? "NOTICE" : "NEWS & RESOURCES"}
                text={category === "166" ? "공지사항" : "관련자료"}
            />
            <div className="p-[28px_0_100px] md:p-[48px_0_160px]">
                <div className="p-[20px] rounded-[20px] bg-white md:p-[40px] xl:p-[60px] xl:rounded-[40px]">
                    {isInitialLoading ? (
                        <div className="h-[400px] flex justify-center items-center">
                            <LoadingSpinner console />
                        </div>
                    ) : (
                        <>
                            <div className="pb-[20px] border-b border-[#ddd] flex flex-col gap-[12px] md:gap-[15px]">
                                <p className="text-[20px] font-[500] md:text-[28px]">{info.b_title}</p>
                                <ul className="flex items-center gap-[16px] text-[16px] text-[#999] font-[500] md:text-[18px]">
                                    <li>{info.m_name}</li>
                                    {category === "166" && (
                                        <>
                                            <li className="w-[1px] h-[16px] bg-[#ddd]"></li>
                                            <li className="text-[14px]">{info.b_reg_date}</li>
                                        </>
                                    )}
                                </ul>
                            </div>
                            <div
                                className="py-[32px] min-h-[200px] md:px-[24px]"
                                dangerouslySetInnerHTML={{ __html: info.b_contents }}
                            />
                            {info.b_file.length > 0 && (
                                <div className="py-[25px] flex">
                                    <p className="text-[20px] font-[500] w-[120px]">첨부파일</p>
                                    <ul className="flex-1 flex flex-col gap-[5px]">
                                        {info.b_file.map((file, i) => (
                                            <li key={`file_${i}`}>
                                                <button
                                                    type="button"
                                                    className="text-[18px] text-[#999] text-left"
                                                    onClick={() =>
                                                        handleFileDownload(file.idx.toString(), file.original_name)
                                                    }
                                                >
                                                    {file.original_name}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                    <div className="border-t border-[#222] flex justify-center py-[20px] md:pt-[30px]">
                        <button
                            type="button"
                            className="w-[240px] h-[52px] text-[#666] font-[500] border border-[#ddd]"
                            onClick={() => navigate(-1)}
                        >
                            목록
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
